import { has, includes, isNil, toLower } from 'lodash';
import useConnections from 'new/hooks/adapters/useConnections';
import React, {
  createContext,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

export interface SelectConnectionProps {
  children?: ReactNode;
  flowType?: string;
  overrideConnections?: string[];
  fixedCategory?: string;
  connectionType: 'SOURCE' | 'SINK';
  selectedConnection?: string;
  onConnectionSelected?: (connection: string, connectionCategory: string) => void;
}

export const SelectConnectionContext = createContext({
  categories: [] as string[],
  selectedCategory: 'All',
  setSelectedCategory: (() => {}) as (category: string) => void,
  onConnectionSelected: (() => {}) as (connection: string, connectionCategory?: string) => void,
  connectionsByCategory: {} as Record<string, string[]>,
  connectionsComingSoon: [] as string[],
  selectedConnection: null as string | null,
  connections: [] as string[],
  isLoadingVendors: false,
});

export function useSelectConnection() {
  return useContext(SelectConnectionContext);
}

export default useSelectConnection;

export const SelectConnectionProvider = (props: PropsWithChildren<SelectConnectionProps>) => {
  const { children } = props;

  const metas = useSelector((state: any) => state.paging.metaInfoById);
  // Check if vendors are loading
  const isLoadingVendors = useSelector((state: any) => !state.vendors?.vendors?.lastUpdated);

  const [selectedCategory, setSelectedCategory] = useState('All');
  const [search, setSearch] = useState('');

  const { categories, connectionsByCategory, connectionsComingSoon } = useConnections(
    props.connectionType,
    props.flowType,
    props.overrideConnections,
  );
  const connections = useMemo(() => {
    if (!search) {
      return connectionsByCategory[selectedCategory];
    }
    return connectionsByCategory[selectedCategory].filter((item) => {
      const meta = metas[item];
      if (isNil(meta) || !has(meta, 'displayName')) {
        return false;
      }

      return includes(toLower(meta.displayName), search.toLowerCase());
    });
  }, [search, selectedCategory, connectionsByCategory]);

  const providerValue = useMemo(() => {
    return {
      categories,
      connectionsByCategory,
      connectionsComingSoon,
      connections,
      selectedCategory,
      selectedConnection: props.selectedConnection,
      onConnectionSelected: props.onConnectionSelected,
      setSelectedCategory,
      isLoadingVendors,
    };
  }, [
    categories,
    connectionsByCategory,
    connectionsComingSoon,
    connections,
    selectedCategory,
    props.selectedConnection,
    props.onConnectionSelected,
    isLoadingVendors,
  ]);

  return (
    <SelectConnectionContext.Provider value={providerValue}>
      {React.cloneElement(children as ReactElement, { setSearch })}
    </SelectConnectionContext.Provider>
  );
};
